/* assets/variant-vspecs-refresh.js */

/*
  Keeps the "Variant Specs Table" in sync with the selected variant.

  The table is output by a Custom Liquid block of the main-product section
  (templates/product.del-sample-boxes.json, block "custom_liquid_jrzC4P") from the
  selected variant's custom.vspecs metafield, inside:
    <div id="ProductSpecs-{{ section.id }}" data-section-id="{{ section.id }}">

  After a variant change, product-info.js (v16) only swaps price, SKU, inventory,
  volume pricing, quantity rules, media and the buy button, so custom blocks would keep
  showing the first variant's specs. It does, however, fetch the whole section for the
  new variant and publish it on PUB_SUB_EVENTS.variantChange ({ sectionId, html, variant }).
  This script copies the specs wrapper out of that HTML, so no extra request is made.
  A wrapper in another section of the page (not inside <product-info>) is refreshed by
  fetching its own section for the new variant, like the original Refresh 9 script did.
*/

(function () {
  'use strict';

  const WRAPPER_SELECTOR = '[id^="ProductSpecs-"][data-section-id]';

  // Section id used in fetched section HTML. In the quick add modal, product-info's
  // data-section is prefixed with "quickadd-" and the real id is in data-original-section.
  function getSourceSectionId(productInfo) {
    return productInfo.dataset.originalSection || productInfo.dataset.section;
  }

  function getWrapper(productInfo) {
    const wrapper = productInfo.querySelector(`#ProductSpecs-${productInfo.dataset.section}`);
    return wrapper && wrapper.matches(WRAPPER_SELECTOR) ? wrapper : null;
  }

  function getVariantIdInput(productInfo) {
    return productInfo.querySelector(`#product-form-${productInfo.dataset.section} input[name="id"]`);
  }

  function copySpecs(html, productInfo) {
    const destination = getWrapper(productInfo);
    const source = html.getElementById(`ProductSpecs-${getSourceSectionId(productInfo)}`);
    if (!destination || !source) return;

    destination.innerHTML = source.innerHTML;
  }

  // A wrapper placed in another section of the product template (e.g. a Custom Liquid
  // section) is not part of the HTML product-info.js fetched, so fetch its own section,
  // as the original script did. Only runs for the main product and only if such a wrapper exists.
  const standaloneRequests = new WeakMap();

  function refreshStandaloneWrappers(productInfo, variant) {
    if (productInfo.dataset.originalSection || !productInfo.id.startsWith('MainProduct-')) return;

    document.querySelectorAll(WRAPPER_SELECTOR).forEach((wrapper) => {
      if (wrapper.closest('product-info')) return;

      standaloneRequests.get(wrapper)?.abort();
      const abortController = new AbortController();
      standaloneRequests.set(wrapper, abortController);

      fetch(`${productInfo.dataset.url}?variant=${variant.id}&section_id=${wrapper.dataset.sectionId}`, {
        signal: abortController.signal,
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Failed section fetch: ${response.status}`);
          return response.text();
        })
        .then((responseText) => {
          const source = new DOMParser().parseFromString(responseText, 'text/html').getElementById(wrapper.id);
          if (source) wrapper.innerHTML = source.innerHTML;
        })
        .catch(() => {});
    });
  }

  function onVariantChange({ data: { sectionId, html, variant } = {} } = {}) {
    if (!sectionId || !html || !variant) return;

    document.querySelectorAll('product-info').forEach((productInfo) => {
      if (getSourceSectionId(productInfo) !== sectionId) return;

      // The same section id can be on the page twice (product page + quick add modal of
      // another product using the same template). product-info.js updates the variant id
      // input before publishing, so only the element that changed matches the new variant.
      const variantIdInput = getVariantIdInput(productInfo);
      if (variantIdInput && variantIdInput.value !== String(variant.id)) return;

      copySpecs(html, productInfo);
      refreshStandaloneWrappers(productInfo, variant);
    });
  }

  // The table and the hidden variant id input are rendered from the same variant, so on
  // page load this only fetches if something changed the variant before this script ran.
  function syncOnLoad() {
    document.querySelectorAll('product-info').forEach((productInfo) => {
      if (!getWrapper(productInfo)) return;

      const variantId = (getVariantIdInput(productInfo)?.value || '').trim();
      const selectedVariantScript = productInfo.querySelector('variant-selects [data-selected-variant]');
      if (!variantId || !selectedVariantScript) return;

      let renderedVariant = null;
      try {
        renderedVariant = JSON.parse(selectedVariantScript.textContent);
      } catch (e) {
        return;
      }
      if (!renderedVariant || String(renderedVariant.id) === variantId) return;

      fetch(`${productInfo.dataset.url}?variant=${variantId}&section_id=${getSourceSectionId(productInfo)}`)
        .then((response) => {
          if (!response.ok) throw new Error(`Failed section fetch: ${response.status}`);
          return response.text();
        })
        .then((responseText) => copySpecs(new DOMParser().parseFromString(responseText, 'text/html'), productInfo))
        .catch(() => {});
    });
  }

  let started = false;

  // Needs subscribe() and PUB_SUB_EVENTS from pubsub.js / constants.js (deferred in theme.liquid).
  function start() {
    if (started || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    if (document.readyState === 'loading') return;

    started = true;
    subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
    syncOnLoad();
  }

  start();
  if (!started) {
    document.addEventListener('DOMContentLoaded', start);
    window.addEventListener('load', start);
  }
})();
